<template>
  <div :class="[$style.container, 'container']">
    <nav v-if="!tag" class="nav-bar">
      <ul>
        <li :class="[{ active: selected === 'filter' }, 'nav-subtypes']">
          <button
            id="filter-button"
            :class="['button-filter', selected === 'filter' ? 'up' : 'down']"
            aria-controls="filter"
            :aria-expanded="selected === 'filter'"
            @click.stop.prevent="toggle('filter')"
          >
            Filters
          </button>
          <ul
            id="filter"
            ref="filtersDropdown"
            class="nav-items"
            role="menu"
            aria-labelledby="filter-button"
          >
            <li :class="{ pressed: !type }">
              <router-link
                :to="{ name: 'AudioSection' }"
                :ga4-event="
                  JSON.stringify({
                    filter_location: 'audio_section',
                    filter_type: 'audio',
                    filter_value: 'all audio',
                    gtm_tag: 'filter',
                  })
                "
              >
                All Audio
              </router-link>
            </li>
            <li :class="{ pressed: type === 'track' }">
              <router-link
                :to="{
                  name: 'AudioTracks',
                  params: {
                    type: 'track',
                  },
                }"
                :ga4-event="
                  JSON.stringify({
                    filter_location: 'audio_section',
                    filter_type: 'audio',
                    filter_value: 'tracks',
                    gtm_tag: 'filter',
                  })
                "
              >
                Tracks
              </router-link>
            </li>
            <li :class="{ pressed: type === 'playlist' }">
              <router-link
                :to="{
                  name: 'AudioPlaylists',
                  params: {
                    type: 'playlist',
                  },
                }"
                :ga4-event="
                  JSON.stringify({
                    filter_location: 'audio_section',
                    filter_type: 'audio',
                    filter_value: 'playlists',
                    gtm_tag: 'filter',
                  })
                "
              >
                Playlists
              </router-link>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
    <header v-if="tag && audioData.length">
      <BackLink component="AudioGrid" group="audio" label="Audio" name="AudioSection"></BackLink>
      <h1 class="space-24-32-above">{{ `Audio Tagged as ${getTagName}` }}</h1>
    </header>
    <header v-else class="unspaced">
      <h1>{{ getTitle }}</h1>
    </header>
    <ul :class="[$style.audioItems, 'space-24-32-above']">
      <li v-for="(audio, index) in audioData" :key="index" class="space-48-64-above">
        <router-link
          :to="{
            name: getRouteName(audio.__typename),
            params: { slug: audio.slug },
          }"
          :ga4-event="
            JSON.stringify({
              click_type: `audio_${audio.__typename.split('_')[1]}`,
              component: 'AudioGrid',
              content_group: 'audio',
              gtm_tag: 'link',
              link_text: `${audio.title}`,
              link_url: `${audio.slug}`,
            })
          "
        >
          <picture :class="[$style.audioCover, 'aspect-container']">
            <img
              v-if="audio.featuredImage"
              :alt="audio.featuredImage.node.altText"
              :src="audio.featuredImage.node.sourceUrl"
              :srcset="audio.imageSrcset"
              class="aspect-image"
              loading="lazy"
              sizes="(min-width: 960px) 470px, 100vw"
            />
          </picture>
          <h2 class="heading-4" v-html="audio.title"></h2>
        </router-link>
        <div>
          <span
            v-if="audio.__typename.split('_')[1] === 'playlist' && audio.tracks"
            class="subtitle-2"
          >
            {{ audio.tracks.length }} {{ audio.tracks.length > 1 ? 'tracks' : 'track' }} |
            <time :datetime="formatTimeDuration(audio.duration)">
              {{ formatDuration(audio.duration) }}
            </time>
          </span>
          <time v-else :datetime="formatTimeDuration(audio.duration)" class="subtitle-2">
            {{ formatDuration(audio.duration) }}
          </time>
        </div>
      </li>
    </ul>
    <GuggenPagination
      :current-page="page"
      :has-bootstrap="total > 0"
      :bootstrap-total="total"
      :per-page="limit"
      :sub-type="getSubType"
      :sub-type-value="tag"
      :total-page-count="totalPageCount"
      post-type="audio"
    ></GuggenPagination>
  </div>
</template>

<script setup lang="ts">
import { AudioData } from '@/types/audio.interface';
import BackLink from '@/components/common/BackLink.vue';
import { BootStrap } from '@/types/global.interface';
import { changeCase, formatDuration, formatTimeDuration } from '@/utils';
import { computed, inject, ref } from 'vue';
import GuggenPagination from '@/components/GuggenPagination.vue';
import { storeToRefs } from 'pinia';
import { useFilterInteractions, useMetaData } from '@/composables/Common.js';
import { useMainStore } from '@/stores/mainStore.js';

const props = withDefaults(
  defineProps<{
    audioData: Array<AudioData>;
    page: number;
    tag: string;
    tagName?: string;
    totalPageCount: number;
    type?: string;
  }>(),
  {
    tag: '',
    type: '',
  },
);

const bootstrap = inject<BootStrap>('bootstrap');

const filtersDropdown = ref(null);

const { setMetaTitle } = useMetaData();
const store = useMainStore();
const { useBootstrap } = storeToRefs(store);

const limit = 27;
const selected = ref('');
const total = ref(0);

const { selectNav } = useFilterInteractions(selected);

const getSubType = computed(() => {
  if (props.type) {
    return `audio_${props.type}`;
  }

  if (props.tag) {
    return 'tag';
  }

  return 'all_audio';
});
const getTagName = computed(() => {
  setMetaTitle(`Audio: ${props.tagName}`);

  return changeCase(props.tagName);
});
const getTitle = computed(() => {
  const title = 'Audio';

  if (props.type === 'track') {
    return `${title} Tracks`;
  }

  if (props.type === 'playlist') {
    return `${title} Playlists`;
  }

  return title;
});

if (useBootstrap.value && bootstrap) {
  total.value = bootstrap.initial.main.pagination.total_posts;
}

function getRouteName(type: string): string {
  const parts = type.split('_');

  return parts[0] + parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
}

function toggle(type: string): void {
  selectNav(type, [filtersDropdown.value]);
}
</script>

<style lang="scss">
@use '@/styles/navigation.module.scss' as *;
</style>

<style lang="scss" module>
.audio-items {
  display: block;
  margin-left: 0;
  padding: 0;

  a {
    display: block;
    text-decoration: none;

    div,
    h2 {
      // Safari hack https://stackoverflow.com/a/61379828
      outline: 2px solid #0000;
      outline-offset: 2px;
    }

    &:hover {
      text-decoration: underline;
    }

    &:focus {
      background-color: transparent;
      color: $white;

      div,
      h2 {
        @include focus-outline;
      }

      h2 {
        background-color: $black;
        color: $white;
      }
    }
  }

  li {
    list-style: none;

    &:first-of-type {
      margin-top: 0;
    }
  }
}

.break {
  word-break: break-all;
}

.filters {
  a {
    margin-right: 1rem;
    padding: 0.5rem px-to-rem(23);
  }
}

@media (min-width: $break-point) {
  .audio-items {
    @include grid;
    grid-row-gap: 3rem;
    margin-top: 0;

    li {
      border-bottom: initial;
      grid-column: span 4;
      margin-top: initial;
      padding-bottom: initial;
    }
  }

  .container {
    @include grid;

    > * {
      grid-column: span 12;
    }
  }

  .filters {
    a {
      padding: 1rem 2.875rem 1rem 3rem;
    }
  }
}
</style>
